/**
 * ReparentCommand
 *
 * A structural move as one undo entry: the object, the parent and sibling
 * seat it had, the ones it has now, and on each side its name, its authored
 * local transform and the state poses over it. Undo and redo put back
 * exactly those, rather than re-deriving them, so a world-preserving move
 * round-trips to the number, a state showing or not.
 *
 * Every node is held as a shared identity ref, never as a raw SceneObject:
 * an operation that deletes and restores a parent in the same undo step
 * (a wrapper swap) rebinds the ref, so the move lands on the live instance.
 * Replayed delete commands acquire these same refs for every descendant they
 * find by uid, so recreating a parent rebinds this command. A scene that built
 * its own copy of the recorded nodes holds other uids, so there the replayed
 * delete shares nothing, and a replayed command finds its parent again by the
 * recorded path. Live commands never substitute an unrelated object at a path.
 *
 * Undo and redo put the object back in a seat it held, so they ask neither
 * the host's reparent policy nor the scene-reference guard: both allowed it
 * there once, and a refused undo would block every older entry on the stack.
 * A new move is theirs to refuse: {@link ReparentCommand.perform}, and the
 * first redo of a replayed command, which applies a recorded move to a scene
 * that has not made it.
 *
 * Layout does not listen to reparenting, so the command re-solves the
 * nearest Layout on BOTH sides, on do, undo and redo. Without it a node
 * moved into a container waits for some unrelated edit to be arranged, and
 * the container it left keeps its old size (#950 §3.3).
 */

import type { UndoCommand, UndoOptions, SerializedCommand } from './UndoCommand';
import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import type { TraceScene } from '../../core/TraceScene';
import { TransformCommand, applySnapshot } from './TransformCommand';
import type { TransformSnapshot } from './TransformCommand';
import { invalidateNearestLayout } from '../../behaviors/layout';
import { registerCommandCodec } from './CommandCodec';
import { Properties } from '../../behaviors/properties';
import type { PropertiesData } from '../../scenedata/behaviors/propertiesdata';
import { Utility } from '../../utils/Utility';

const CODEC_TYPE = 'Reparent';

/** Where an object sits: its parent, its place among the parent's children, its name and local transform. */
export interface ReparentSeat {
    parent: SceneObject;
    index: number;
    name: string;
    /** The authored local transform: Default, whatever state shows. */
    transform: TransformSnapshot;
    /** The poses in the parent's frame that Properties holds over Default, when it holds any. */
    poses?: StatePoses;
}

/** The parts of an object's Properties expressed in its parent's frame. */
type StatePoses = Pick<PropertiesData, 'states' | 'transform'>;

export interface ReparentOptions {
    /** The seat among the new parent's children. Default: last. */
    index?: number;
    /**
     * Keep the object's world pose (the default), or keep its local
     * transform as it is.
     */
    preserveWorld?: boolean;
    /**
     * The name to leave the object with at the destination, when the move
     * renamed it to keep its siblings' names unique and the name it came
     * with is free there. Without it the destination's rule stands.
     */
    name?: string;
}

interface SeatPayload {
    parentPath: string;
    index: number;
    name: string;
    transform: TransformSnapshot;
    poses?: StatePoses;
}

interface ReparentPayload {
    from: SeatPayload;
    to: SeatPayload;
}

interface HeldSeat {
    parent: SceneObjectRef;
    /**
     * Where the parent stood when the move was recorded. A recording is made
     * after the fact, and a later step in the same operation can delete the
     * parent (the old copy of a swapped wrapper), which leaves nothing for the
     * ref to answer.
     */
    parentPath: string;
    index: number;
    name: string;
    transform: TransformSnapshot;
    poses?: StatePoses;
}

export class ReparentCommand implements UndoCommand {
    readonly label: string;
    private identity: SceneObjectRef;
    private from: HeldSeat;
    private to: HeldSeat;
    private replayed: boolean;
    /** A replayed command whose move this scene has not made yet: its first redo is a new move. */
    private unapplied: boolean;

    /**
     * Record a move that has already happened (the convention every command
     * here follows). {@link ReparentCommand.perform} makes the move and
     * records it in one call.
     *
     * Undo and redo restore these seats without asking the host, so `to` must
     * be where the object stands now: a seat it holds, not one handed in.
     * Throws otherwise. `replayed` is the codec's: a replayed command is made
     * before its move, and its first redo is asked.
     */
    constructor(sceneObject: SceneObject, from: ReparentSeat, to: ReparentSeat, replayed = false) {
        if (!replayed && sceneObject.getParent() !== to.parent) {
            throw new Error(`[ReparentCommand] "${sceneObject.name}" does not stand under "${to.parent.name}": a move is recorded after it is made`);
        }
        const registry = sceneObject.traceScene.objectIdentityRegistry;
        this.identity = registry.refFor(sceneObject);
        const hold = (seat: ReparentSeat): HeldSeat => ({
            parent: registry.refFor(seat.parent),
            parentPath: seat.parent.getPath(),
            index: seat.index,
            name: seat.name,
            transform: structuredClone(seat.transform),
            poses: structuredClone(seat.poses),
        });
        this.from = hold(from);
        this.to = hold(to);
        this.replayed = replayed;
        this.unapplied = replayed;
        this.label = `Reparent ${to.name}`;
    }

    /**
     * Move `sceneObject` under `parent` and return the command that undoes it.
     *
     * Throws, with nothing changed, when the move is refused: a host reparent
     * policy veto, or a parent inside a reference subtree.
     */
    static perform(sceneObject: SceneObject, parent: SceneObject, options: ReparentOptions = {}): ReparentCommand {
        const scene = sceneObject.traceScene;
        const from = seatOf(sceneObject);
        // Through the setParent interceptor either way, so a host reparent
        // policy is honoured; a veto reverts the move in place.
        scene.reparentSceneObjectPreservingWorld(sceneObject, parent);
        if (sceneObject.getParent() !== parent) {
            throw new Error(`[ReparentCommand] a reparent policy refused to move "${sceneObject.name}"`);
        }
        if (options.preserveWorld === false) {
            applySnapshot(sceneObject, from.transform);
            // The hop re-framed the poses, and this move keeps every local
            // value. A commit merges its data before it first awaits, so the
            // seat recorded below already holds the poses put back, and it
            // reports a failure in what it resolves to rather than rejecting.
            void restorePoses(sceneObject, from.poses);
        }
        if (options.index !== undefined) seatAmongSiblings(parent, sceneObject, options.index);
        if (options.name !== undefined && sceneObject.name !== options.name) {
            scene.renameSceneObject(sceneObject, options.name);
        }
        invalidateNearestLayout(from.parent);
        invalidateNearestLayout(parent);
        return new ReparentCommand(sceneObject, from, seatOf(sceneObject));
    }

    undo(options?: UndoOptions): Promise<void> | void {
        return this.apply(this.from, this.to, options?.silent === true, false);
    }

    redo(): Promise<void> | void {
        const applied = this.apply(this.to, this.from, false, this.unapplied);
        this.unapplied = false;
        return applied;
    }

    serialize(): SerializedCommand {
        const payload: ReparentPayload = {
            from: this.payloadOf(this.from),
            to: this.payloadOf(this.to),
        };
        return { type: CODEC_TYPE, data: payload };
    }

    private payloadOf(seat: HeldSeat): SeatPayload {
        // A parent deleted directly leaves the ref bound to its disposed
        // instance, whose path is just its name: only a live one is asked.
        const parent = seat.parent.get();
        return {
            parentPath: parent && !parent.transform.isDisposed() ? parent.getPath() : seat.parentPath,
            index: seat.index,
            name: seat.name,
            transform: structuredClone(seat.transform),
            poses: structuredClone(seat.poses),
        };
    }

    private apply(target: HeldSeat, other: HeldSeat, silent: boolean, newMove: boolean): Promise<void> | void {
        const object = this.identity.get();
        if (!object) return;
        const scene = object.traceScene;
        const parent = liveParent(target, this.replayed ? scene : null);
        if (!parent) {
            throw new Error(`[ReparentCommand] "${object.name}" was not moved: nothing stands at "${target.parentPath}" to take it`);
        }
        // A replay's first redo applies a recorded move to a scene that has
        // not made it: that is a new move, and the host's policy and the
        // reference guard are asked, as `perform` asks them.
        if (newMove && object.getParent() !== parent && !scene._isReparentAllowed(object, parent)) {
            throw new Error(`[ReparentCommand] a reparent policy refused to move "${object.name}"`);
        }
        // The recorded name, unless a sibling has taken it since; given before
        // the move, so nothing that hears of the move sees two siblings alike.
        const taken = new Set(parent.getChildren().filter(child => child !== object).map(child => child.name));
        const name = taken.has(target.name) ? Utility.generateUniqueName(target.name, taken) : target.name;
        const oldName = object.name;
        if (oldName !== name) object._setName(name);
        // Any other undo or redo puts the object back in a seat it held, so
        // neither the policy nor the guard is asked again: they let it stand
        // there once, and a refusal now would strand every older undo behind
        // this one. The raw move skips both, and keeps the local transform
        // restored below.
        object.setParent(parent);
        if (oldName !== name) scene._notifyObjectRenamed(object, oldName, name);
        seatAmongSiblings(parent, object, target.index);
        applySnapshot(object, target.transform);
        const left = liveParent(other, this.replayed ? scene : null);
        if (left) invalidateNearestLayout(left);
        invalidateNearestLayout(parent);
        if (!silent) scene.select(object);
        // The raw move re-frames nothing, so the poses come back as recorded.
        return restorePoses(object, target.poses);
    }
}

/**
 * The parent a seat names, alive. A node the ref still holds after it was
 * disposed is as good as none. Only a replayed command may fall back to the
 * recorded path when its parent did not exist as the recording was decoded.
 */
function liveParent(seat: HeldSeat, replayScene: TraceScene | null): SceneObject | null {
    const held = seat.parent.get();
    if (held && !held.transform.isDisposed()) return held;
    return replayScene?.resolveSceneObject(seat.parentPath) ?? null;
}

function seatOf(sceneObject: SceneObject): ReparentSeat {
    const parent = sceneObject.getParent();
    if (!parent) throw new Error(`[ReparentCommand] "${sceneObject.name}" has no parent to move from`);
    const props = sceneObject.getBehavior(Properties);
    const authored = props?.getAuthoredTransform();
    const poses = props && posesOf(props);
    return {
        parent,
        index: parent.getChildren().indexOf(sceneObject),
        name: sceneObject.name,
        transform: authored
            ? {
                position: authored.position.asArray(),
                rotation: authored.rotation.asArray(),
                scale: authored.scale.asArray(),
            }
            : TransformCommand.capture(sceneObject),
        ...(poses && { poses }),
    };
}

function posesOf(props: Properties): StatePoses | undefined {
    const { states, transform } = props.getData();
    if (!states && !transform) return undefined;
    return { ...(states && { states }), ...(transform && { transform }) };
}

/**
 * Put back the poses a seat recorded, after a snapshot put the node at
 * Default, and show again the state that showed: the snapshot doesn't
 * re-pose it, and the commit runs only when there are poses.
 */
async function restorePoses(sceneObject: SceneObject, poses: StatePoses | undefined): Promise<void> {
    const props = sceneObject.getBehavior(Properties);
    if (!props) return;
    if (poses) await props.commit(poses);
    // Re-pose by stepping the layer that shows off and back on.
    const pick = props.getEditorState();
    if (pick !== undefined) {
        if (pick === null) return;
        props.setEditorState(null, true);
        props.setEditorState(pick, true);
        return;
    }
    const shown = props.getRuntimeState();
    if (shown === undefined) return;
    props.setState(undefined, true);
    props.setState(shown, true);
}

/**
 * Put `child` (already under `parent`) at `index` among its siblings.
 *
 * Append the child and then the siblings that belong after it; a same-parent
 * move has not appended the child yet. Sibling order is flow order to the
 * layout solver. An index equal to the sibling count re-appends the child last;
 * a negative index or one beyond the count leaves it where it is.
 */
export function seatAmongSiblings(parent: SceneObject, child: SceneObject, index: number): void {
    const siblings = parent.getChildren().filter(sibling => sibling !== child);
    if (index < 0 || index > siblings.length) return;
    for (const sibling of [child, ...siblings.slice(index)]) {
        const transform = sibling.transform;
        const node = transform.parent;
        transform.parent = null;
        transform.parent = node;
    }
}

registerCommandCodec(CODEC_TYPE, (data, scene): ReparentCommand => {
    const p = data as ReparentPayload;
    const resolve = (path: string, what: string): SceneObject => {
        const found = scene.resolveSceneObject(path);
        if (!found) throw new Error(`[ReparentCommand] Cannot resolve ${what} "${path}"`);
        return found;
    };
    // Replay runs redo() against the state before the move: the object is
    // where `from` says.
    const fromParent = resolve(p.from.parentPath, 'parent');
    const object = resolve(`${p.from.parentPath}/${p.from.name}`, 'object');
    return new ReparentCommand(
        object,
        { ...p.from, parent: fromParent },
        { ...p.to, parent: resolve(p.to.parentPath, 'parent') },
        true,
    );
});
